import { type FC, Suspense, lazy } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useAppSelector } from '../hooks/useReactReduxHooks';

// components
import { PdpRespSidebarDetailsAbout } from './PdpRespSidebarDetailsAbout';
import { PdpRespSidebarDetailsCustomization } from './PdpRespSidebarDetailsCustomization';

// types
import { type PdpRespSidebarDetails_item$key as Item } from './__generated__/PdpRespSidebarDetails_item.graphql';
import { type PdpRespSidebarDetails_user$key as User } from './__generated__/PdpRespSidebarDetails_user.graphql';

const PdpRespSidebarDetailsPricingGuidance = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpRespSidebarDetailsPricingGuidance" */ './PdpRespSidebarDetailsPricingGuidance'
        )
);

const itemFragment = graphql`
    fragment PdpRespSidebarDetails_item on Item {
        ...PdpRespSidebarDetailsAbout_item
        ...PdpRespSidebarDetailsCustomization_item
        ...PdpRespSidebarDetailsPricingGuidance_item @include(if: $afterDisplay)
    }
`;

const userFragment = graphql`
    fragment PdpRespSidebarDetails_user on User {
        ...PdpRespSidebarDetailsAbout_user
        ...PdpRespSidebarDetailsCustomization_user
    }
`;

export const PdpRespSidebarDetails: FC<{
    item: Item;
    user: User | null;
    skuId: string | null;
    itemQuantity: number | null;
}> = ({ item: itemRef, user: userRef, skuId }) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef) || null;
    const { afterDisplay } = useAppSelector(relay => relay?.relayVariables?.variables || {});

    return (
        <>
            <PdpRespSidebarDetailsAbout item={item} user={user} skuId={skuId} />
            <PdpRespSidebarDetailsCustomization item={item} user={user} />

            {afterDisplay && (
                <Suspense fallback={null}>
                    <PdpRespSidebarDetailsPricingGuidance item={item} skuId={skuId} />
                </Suspense>
            )}
        </>
    );
};
